/**
 * Accountability Circle Widget
 * 
 * Small groups of 4-6 sisters matched by focus areas for mutual support.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Users, ArrowRight, Loader2, MessageCircle, Sparkles, 
  UserPlus, LogOut, Target, Heart, Bell, Shuffle, Clock
} from 'lucide-react';

const API_URL = window.location.origin;

const AccountabilityCircleWidget = ({ className = '' }) => {
  const [circle, setCircle] = useState(null);
  const [focusAreas, setFocusAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [leaveStep, setLeaveStep] = useState(1); // 1 = confirm, 2 = reason, 3 = options
  const [leaveReason, setLeaveReason] = useState('');
  const [selectedFocusArea, setSelectedFocusArea] = useState('');
  const [hasNewActivity, setHasNewActivity] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [waitlistStatus, setWaitlistStatus] = useState(null);

  useEffect(() => {
    fetchCircle();
    fetchFocusAreas();
    checkWaitlistStatus();
  }, []);
  
  // Check for new activity when circle data loads
  useEffect(() => {
    if (circle?.id) {
      checkNewActivity();
    }
  }, [circle?.id]);

  const checkNewActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      // Check for recent messages/checkins since user's last visit
      const response = await axios.get(
        `${API_URL}/api/accountability-circle/${circle.id}/unread-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const count = response.data.unread_count || 0;
      setUnreadCount(count);
      setHasNewActivity(count > 0);
    } catch (error) {
      // Silently fail - don't disrupt widget if this optional feature fails
      console.log('Could not check unread count:', error.message);
    }
  };

  const fetchCircle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accountability-circle/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns {in_circle: boolean, circle: object|null}
      if (response.data.in_circle && response.data.circle) {
        setCircle(response.data.circle);
      } else {
        setCircle(null);
      }
    } catch (error) {
      console.error('Failed to fetch accountability circle:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFocusAreas = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/accountability-circle/focus-areas`);
      setFocusAreas(response.data.focus_areas || []);
    } catch (error) {
      console.error('Failed to fetch focus areas:', error);
    }
  };

  const checkWaitlistStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accountability-circle/waitlist-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitlistStatus(response.data);
    } catch (error) {
      console.log('Could not check waitlist status:', error.message);
    }
  };

  const handleJoinCircle = async () => {
    setJoining(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accountability-circle/join`,
        { focus_area: selectedFocusArea || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        toast.success(response.data.message || 'Welcome to your circle!');
        setCircle(response.data.circle);
        setShowJoinDialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join circle');
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveCircle = async (rejoinOption = null) => {
    setLeaving(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/accountability-circle/leave`;
      if (rejoinOption) {
        url += `?rejoin_option=${rejoinOption}`;
      }
      
      const response = await axios.post(url, {
        reason: leaveReason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        // Show appropriate success message
        if (response.data.rejoined) {
          toast.success(`You've joined ${response.data.new_circle?.name || 'a new circle'}!`);
          setCircle(response.data.new_circle);
        } else if (response.data.waitlisted) {
          toast.success("You've been added to the waitlist for a new circle.");
          setCircle(null);
          checkWaitlistStatus();
        } else {
          toast.success(response.data.message || 'You have left the circle');
          setCircle(null);
        }
        setShowLeaveDialog(false);
        setLeaveStep(1);
        setLeaveReason('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave circle');
    } finally {
      setLeaving(false);
    }
  };

  const handleJoinRandom = async () => {
    setJoining(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/accountability-circle/join-random`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.error) {
        toast.error(response.data.error);
      } else if (response.data.waitlisted) {
        toast.info(response.data.message || "You've been added to the waitlist.");
        checkWaitlistStatus();
      } else {
        toast.success(response.data.message || 'Welcome to your new circle!');
        setCircle(response.data.circle);
        setWaitlistStatus(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join circle');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Card className={`card-base ${className}`} data-testid="accountability-circle-widget">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
        </CardContent>
      </Card>
    );
  }

  // User is not in a circle - show join prompt or waitlist status
  if (!circle) {
    return (
      <>
        <Card className={`card-base ${className}`} data-testid="accountability-circle-widget">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                {waitlistStatus?.on_waitlist ? (
                  <>
                    <h3 className="font-heading text-lg font-semibold text-forest">
                      You're on the Waitlist
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      You're #{waitlistStatus.position} in line. We'll match you with like-minded sisters as soon as a circle opens up!
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                      <Badge variant="outline" className="text-purple-600 border-purple-300">
                        <Clock className="h-3 w-3 mr-1" />
                        Position #{waitlistStatus.position}
                      </Badge>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-heading text-lg font-semibold text-forest">
                      Find Your Accountability Circle
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Join a small group of 4-6 sisters who share your focus area for mutual support and encouragement.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button 
                        onClick={() => setShowJoinDialog(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="join-circle-btn"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Join a Circle
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleJoinRandom}
                        disabled={joining}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                        data-testid="join-random-btn"
                      >
                        {joining ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Shuffle className="h-4 w-4 mr-2" />
                        )}
                        Match Me Randomly
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Join Circle Dialog */}
        <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Join Your Accountability Circle
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                You'll be matched with 4-6 sisters who share your focus area. Together, you'll support each other's journey.
              </p>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest">
                  Select your focus area (optional)
                </label>
                <Select value={selectedFocusArea} onValueChange={setSelectedFocusArea}>
                  <SelectTrigger>
                    <SelectValue placeholder="Auto-match based on my profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {focusAreas.map(area => (
                      <SelectItem key={area.id} value={area.id}>
                        {area.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 text-purple-700 text-sm font-medium mb-2">
                  <Sparkles className="h-4 w-4" />
                  You'll earn 25 diamonds for joining!
                </div>
                <p className="text-xs text-purple-600">
                  Plus bonus points for weekly check-ins with your circle.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
                Not Now
              </Button>
              <Button 
                onClick={handleJoinCircle}
                disabled={joining}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {joining ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Finding your circle...</>
                ) : (
                  <><Heart className="h-4 w-4 mr-2" />Join My Circle</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // User is in a circle - show circle info
  return (
    <Card className={`card-base ${className}`} data-testid="accountability-circle-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            Your Accountability Circle
            {hasNewActivity && (
              <span className="relative" data-testid="circle-notification-indicator">
                <Bell className="h-4 w-4 text-terracotta animate-pulse" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
            )}
          </CardTitle>
          <Badge className="bg-purple-100 text-purple-700">
            {circle.focus_area_label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Circle Name */}
          <div className="p-3 bg-purple-50 rounded-lg">
            <h4 className="font-medium text-forest">{circle.name}</h4>
            <p className="text-xs text-gray-500 mt-1">
              {circle.member_count} sisters supporting each other
            </p>
          </div>

          {/* Circle Members */}
          <div>
            <p className="text-sm font-medium text-gray-600 mb-2">Your Circle Sisters</p>
            <div className="flex flex-wrap gap-2">
              {circle.members?.map((member, idx) => (
                <div 
                  key={member.id || idx}
                  className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={member.profile_image?.startsWith('http') ? member.profile_image : (member.profile_image ? `${API_URL}${member.profile_image}` : undefined)} />
                    <AvatarFallback className="bg-purple-100 text-purple-600 text-xs">
                      {member.full_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-forest font-medium">
                    {member.full_name?.split(' ')[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <Link to={`/accountability-circle/${circle.id}`}>
              <Button variant="outline" size="sm" className="text-purple-600 border-purple-200 hover:bg-purple-50">
                <MessageCircle className="h-4 w-4 mr-2" />
                Open Circle Chat
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowLeaveDialog(true)}
              className="text-gray-500 hover:text-red-600"
            >
              <LogOut className="h-4 w-4 mr-1" />Leave
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Leave Circle Dialog - Multi-step */}
      <Dialog open={showLeaveDialog} onOpenChange={(open) => {
        setShowLeaveDialog(open);
        if (!open) {
          setLeaveStep(1);
          setLeaveReason('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          {/* Step 1: Confirmation */}
          {leaveStep === 1 && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-forest">
                  She says...
                </DialogTitle>
                <DialogDescription className="text-gray-600 pt-2">
                  Are you sure you want to leave your accountability circle?
                </DialogDescription>
              </DialogHeader>
              
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="ghost" onClick={() => setShowLeaveDialog(false)}>
                  No, stay in my circle
                </Button>
                <Button 
                  onClick={() => setLeaveStep(2)}
                  className="bg-terracotta hover:bg-terracotta/90 text-white"
                >
                  Yes, I want to leave this room
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step 2: Reason (Optional) */}
          {leaveStep === 2 && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-forest">
                  Would you like to share why?
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Your feedback helps us improve the circle experience. (Optional)
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4">
                <Textarea
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  placeholder="Share your thoughts... (optional)"
                  className="min-h-[100px]"
                />
              </div>
              
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="ghost" onClick={() => setLeaveStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={() => setLeaveStep(3)}
                  className="bg-forest hover:bg-forest/90 text-white"
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Step 3: Rejoin Options */}
          {leaveStep === 3 && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-forest">
                  What would you like to do next?
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Your sisters will miss you! Choose how you'd like to proceed:
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-3 py-4">
                <Button
                  onClick={() => handleLeaveCircle('random')}
                  disabled={leaving}
                  className="w-full justify-start h-auto p-4 bg-purple-50 hover:bg-purple-100 text-left"
                  variant="outline"
                >
                  <div className="flex items-start gap-3">
                    <Shuffle className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-forest">Join Another Circle</p>
                      <p className="text-xs text-gray-500 font-normal">
                        Leave this circle and get matched with a new group of sisters
                      </p>
                    </div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => handleLeaveCircle('waitlist')}
                  disabled={leaving}
                  className="w-full justify-start h-auto p-4 bg-amber-50 hover:bg-amber-100 text-left"
                  variant="outline"
                >
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-forest">Join Waitlist</p>
                      <p className="text-xs text-gray-500 font-normal">
                        Leave now and be placed on the waitlist for future matching
                      </p>
                    </div>
                  </div>
                </Button>
                
                <Button
                  onClick={() => handleLeaveCircle(null)}
                  disabled={leaving}
                  className="w-full justify-start h-auto p-4 text-left"
                  variant="outline"
                >
                  <div className="flex items-start gap-3">
                    <LogOut className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-forest">Just Leave</p>
                      <p className="text-xs text-gray-500 font-normal">
                        Leave without joining another circle right now
                      </p>
                    </div>
                  </div>
                </Button>
              </div>
              
              <DialogFooter>
                <Button variant="ghost" onClick={() => setLeaveStep(2)} disabled={leaving}>
                  Back
                </Button>
              </DialogFooter>
              
              {leaving && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AccountabilityCircleWidget;
